import Button from '@/components/Button';
import Input from '@/components/Input';
import KeyHandler from '@/components/KeyHandler';
import { FiX, HiOutlineSparkles, IoSend, LuListRestart } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import appendContextToAiMessages from '@/lib/appendContextToAiMessages';
import { MAIN_KEY } from '@/lib/getMainKey';
import { postAiChatbot } from '@/redux/actions/gravityActions';
import { clearAiChat, sendAiChatMessage, toggleAiChat } from '@/redux/actions/uiActions/aiChatActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import * as Popover from '@radix-ui/react-popover';
import { lazy, memo, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ChatbotAnchorTag from './aiChatbot/ChatbotAnchorTag';

const Markdown = lazy(() => import('react-markdown'));

const getLastMessageRole = (messages) => {
  return messages?.[messages.length - 1]?.role;
};

const AiChatbot = memo(({ isToolbar = false, isWorkflowEditor = false }) => {
  const messageRef = useRef(null);
  const open = useSelector(selectors.aiChatOpen);
  const messages = useSelector(memoizeSelector('aiChatMessages', selectors.aiChatMessages));
  const aiContext = useSelector(memoizeSelector('aiContext', selectors.aiContext));
  const isLoading = useArgSelector(selectors.isLoading, 'postAiChatbot');
  const [message, setMessage] = useState('');
  const [wasOpened, setWasOpened] = useState(false);
  const messagesEndRef = useRef(null);

  const keyMap = {
    'Meta+i': () => toggleAiChat({ open: !open }),
    'Control+i': () => toggleAiChat({ open: !open })
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    if (e.key === 'Escape') {
      toggleAiChat({ open: false });
    }
  };

  const handleSendMessage = async () => {
    if (!message?.trim() || isLoading) {
      return;
    }
    setMessage('');
    sendAiChatMessage(message);
  };

  const scrollToChatBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 10);
  };

  useEffect(() => {
    scrollToChatBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    if (getLastMessageRole(messages) === 'user' && !isLoading) {
      scrollToChatBottom();
      const messagesWithContext = appendContextToAiMessages(aiContext, messages);
      postAiChatbot({ messages: messagesWithContext });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (open) {
      setWasOpened(true);
      scrollToChatBottom();
      setTimeout(() => {
        messageRef?.current?.focus();
      }, 0);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      toggleAiChat({ open: false });
    };
  }, []);

  return (
    <>
      <KeyHandler keyMap={keyMap} global />
      <Popover.Root open={open}>
        <Popover.Trigger asChild onClick={toggleAiChat}>
          <div className="ai-chatbot-trigger" data-open={open} data-was-opened={wasOpened} data-title="AI Assistant" data-title-shortcut={`${MAIN_KEY} + I`}>
            <IconV2 icon={open ? FiX : HiOutlineSparkles} />
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="ai-chatbot-content" side="top" sideOffset={isToolbar ? 16 : 8} forceMount onCloseAutoFocus={(e) => e.preventDefault()} onKeyDown={handleKeyDown} data-is-workflow-editor={isWorkflowEditor}>
            <div className="header toolbar-handle">
              <div className="text">
                <div className="title">AI Assistant</div>
                <div className="subtitle">Ask anything about workflows</div>
              </div>
              <div className="actions">
                <Button data-title="Restart conversation" className="light filled" icon={LuListRestart} onClick={clearAiChat} />
                <Button data-title="Close" className="light filled" icon={FiX} onClick={() => toggleAiChat({ open: false })} />
              </div>
            </div>
            <ScrollContainerV2 className="chat">
              {messages.map((message, index) => (
                <Suspense key={index}>
                  <Markdown className={`message ${message.role}`} components={{ a: ChatbotAnchorTag }}>
                    {message.content}
                  </Markdown>
                </Suspense>
              ))}
              {isLoading && <div className="message assistant loading" />}
              <div ref={messagesEndRef} />
            </ScrollContainerV2>
            <div className="message-input">
              <Input //
                type="textarea"
                placeholder="Message..."
                className="borderless"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                ref={messageRef}
                autoFocus
                rows={Math.min(Math.ceil(message.length / 55), 6)}
              />
              <Button data-title="Send message" className="medium send-button negative-right" icon={IoSend} onClick={handleSendMessage} />
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
});

AiChatbot.displayName = 'AiChatbot';

export default AiChatbot;
